/**
 * @file 魔方组件专用的 fetch 插件
 *
 * 适用于原魔方的组件
 */

/* eslint-disable no-param-reassign,@typescript-eslint/no-explicit-any */
import Vue from 'vue';
import md5 from 'md5';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { getSearchObj, asyncLoadJs, getUrlParam } from '@tencent/magic-ui-util';
import jsonpAdapter from './jsonp-adapter';

interface RequestOptions extends AxiosRequestConfig {
  kfUrl?: string;
  url: string;
  type: string;
  unique?: boolean;
  config?: AxiosRequestConfig;
}

export interface Res<T> {
  ret?: number;
  msg?: string;
  security_verify: {
    iRet: number;
  };
  frame_resp: {
    ret: number;
    msg: string;
    security_verify: {
      iRet: number;
    };
  };
  data?: T;
}

let loadedAMapJS = false; // 是否加载完js
let firstLoadingAMapJS = true; // 否是第一次请求

const getVftShield = () => new Promise((resolve, reject) => {
  if (!loadedAMapJS && firstLoadingAMapJS) {
    window.jQuery = window.$;
    firstLoadingAMapJS = false; // 马上置为false 只让第一个created组件去请求js
    asyncLoadJs('//vm.gtimg.cn/tencentvideo_v1/script/film/weixin/v3/lib/vftshield.min.js')
      .then(() => {
        loadedAMapJS = true;
        resolve(undefined);
      })
      .catch((e) => {
        firstLoadingAMapJS = true; // 出错置为true 下次进来还是能重新请求js
        reject(e);
      });
  }
  resolve(undefined);
}).then(() => new Promise((resolve, reject) => {
  try {
    if (!window.VftShield) {
      const interval = setInterval(() => {
        if (window.VftShield) {
          clearInterval(interval);
          window.vftShield = new window.VftShield();
          resolve(window.vftShield);
        }
      }, 300);
    } else {
      window.vftShield = new window.VftShield();
      resolve(window.vftShield);
    }
  } catch (e) {
    reject(e);
  }
}));

// 拦截axios，注入上报方法
const injectAxios = (instance: AxiosInstance): AxiosInstance => {
  window.report?.injectAxios?.(instance, {
    cookieGetter() {
      // 可选，默认不上报cookie
      return window.document.cookie;
    },
  });
  return instance;
};

/**
 * trpc请求的错误码在headers中返回，解析错误码和错误信息
 * @param headers 请求返回header
 */
const parseHeaders = (headers: any = {}) => {
  const callType = headers['trpc-call-type'];
  // 中文会乱码
  const errorMsg = decodeURIComponent(escape(headers['trpc-error-msg']));
  const funcRet = parseInt(headers['trpc-func-ret'], 10);
  const messageType = headers['trpc-message-type'];
  const requestId = headers['trpc-request-id'];
  const ret = parseInt(headers['trpc-ret'], 10);
  const version = headers['trpc-version'];
  return {
    callType,
    errorMsg,
    funcRet,
    messageType,
    requestId,
    ret,
    version,
  };
};

/**
 * 处理返回码 抛出异常
 * @param originHeaders
 */
const checkTrpcHeader = (originHeaders = {}) => {
  const headers = parseHeaders(originHeaders);
  if (headers.ret === 0 && headers.funcRet === 0) {
    return true;
  }
  const ret = headers.ret !== 0 ? headers.ret : headers.funcRet;
  const error: any = new Error(`request failed: ${headers.errorMsg} (${ret})`);
  error.ret = ret;
  error.msg = headers.errorMsg;
  error.requestId = headers.requestId;
  throw error;
};


injectAxios(axios);
// 重写axios.create，注入上报方法
const old = axios.create;
axios.create = function (...args) {
  // eslint-disable-next-line prefer-rest-params
  const instance = old.apply(this, args);
  return injectAxios(instance);
};

function getUniqueKey(url: string, data: Record<any, any> = {}) {
  if (url.indexOf('//') === 0) {
    url = `http:${url}`;
  }
  const sUrl = new URL(url);
  const obj: Record<any, any> = {};
  // eslint-disable-next-line no-underscore-dangle
  obj.__ = sUrl.host + sUrl.pathname;
  const searchObj = getSearchObj(sUrl.search ? sUrl.search.substring(1) : '');
  Object.keys(searchObj).forEach((name) => {
    if (name && searchObj[name]) {
      if (name !== 'callback' && name !== '_' && name !== 'g_tk') {
        obj[name] = searchObj[name];
      }
    }
  });

  // eslint-disable-next-line no-restricted-syntax
  for (const i in data) {
    if (data[i]) {
      obj[i] = data[i];
    }
  }

  const ordered: Record<any, any> = {};
  Object.keys(obj)
    .sort()
    .forEach((key) => {
      ordered[key] = obj[key];
    });

  const arr = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const i in ordered) {
    arr.push(`${i}=${ordered[i]}`);
  }

  return md5(arr.join('&'));
}

const service = axios.create({
  withCredentials: true,
  timeout: 7000,
});

const magicRequestHandler = (config: Record<any, any>) => {
  if (
    config.method === 'get'
    && (config.url.includes('fcgi-bin/asyn_activity')
      || config.url.includes('/fcgi-bin/comm_cgi'))
  ) {
    const a = document.createElement('a');
    a.href = config.url;
    const otype = config.params?.otype || getUrlParam('otype', a.href);
    // 魔方和comm_cgi请求，添加otype=xjson
    if (otype === 'json') {
      config.adapter = jsonpAdapter;
    } else {
      config.params = {
        ...(config.params || {}),
        otype: 'xjson',
        _ts: new Date().getTime(),
      };
    }
  } else if (config.otype === 'jsonp') {
    config.adapter = jsonpAdapter;
  }

  return config;
};


const responseHandler = (response: Record<any, any>) => {
  if (response?.config?.originalResponse === true) {
    return response;
  }

  if (response.config.otype === 'trpc') {
    checkTrpcHeader(response.headers);
  }

  return response.data;
};

service.interceptors.request.use(magicRequestHandler);
service.interceptors.response.use(responseHandler);

const createUniqueRequest = () => {
  const requestMap = new Map();
  return (uniqueKey: string, requestFn: any) => (...args: any[]) => {
    if (requestMap.get(uniqueKey)) return requestMap.get(uniqueKey);
    const request = requestFn(...args);
    requestMap.set(uniqueKey, request);
    request
      .then((res: Record<any, any>) => {
        requestMap.delete(uniqueKey);
        return res;
      })
      .catch((err: Record<any, any>) => {
        requestMap.delete(uniqueKey);
        throw err;
      });
    return request;
  };
};

const uniqueRequest = createUniqueRequest();

export default {
  request(options: RequestOptions): Promise<Res<{}>> {
    let requestFn = options.type === 'POST' ? this.post : this.get;

    if (options.unique !== false && options.type !== 'POST') {
      requestFn = uniqueRequest(getUniqueKey(options.url, options.data), requestFn);
    }

    return requestFn(options).then((res: Res<any>) => this.securityHandler(res, options));
  },

  get(options: RequestOptions): Promise<Res<any>> {
    const { data, ...rest } = options || {};
    return service.get(options.url, { ...rest, params: rest.params || data });
  },

  post(options: RequestOptions): Promise<Res<any>> {
    return service.post(options.url, options.data, options.config);
  },

  async securityHandler(res: Res<any>, options: RequestOptions): Promise<Res<any>> {
    /* eslint-disable camelcase */
    let securityVerify = res?.frame_resp?.security_verify || { iRet: 0 };
    if (securityVerify.iRet === 0 && res?.security_verify) {
      securityVerify = res?.security_verify || {};
    }
    const ret = securityVerify.iRet;

    if (ret !== 0) {
      if (ret === 101) {
        Vue.prototype.confirm({
          msg: '您的账号存在安全风险，请联系客服处理。',
          confirmText: '联系客服',
          confirm: () => {
            const kfUrl = options.kfUrl || '//kf.qq.com/touch/video/index.html';
            window.location.href = kfUrl;
          },
        });
      } else if ([102, 103, 104, 105].includes(ret)) {
        const shield: any = await getVftShield();
        shield.setStrategy(securityVerify);
        await shield.run();
        return this.request(options);
      }
      this.reqErrorTip(ret);
      const err = new Error('security error');
      // @ts-expect-error
      err.response = res;
      // @ts-expect-error
      err.options = options;
      throw err;
    }
    return res;
  },

  /**
   * 错误提示
   * @param errorCode
   */
  reqErrorTip(errorCode: number) {
    const errMsgMap: Record<string, string> = {
      '-1007': '当日手机验证频率已达上限，请24小时后重试',
      '-1005': '验证码验证失败，请重试',
      '-1021': '该手机号30天内已经替换过一次绑定关系，每30天仅限更换一次',
    };
    const msg = errMsgMap[`${errorCode}`];
    if (!msg) return;
    Vue.prototype.alert({
      msg,
    });
  },
};

